/**
 * Billboard crowd — the export's own crowd design, and the fix for two defects
 * the mesh crowd could not solve.
 *
 * ── Why this replaces the instanced 3D crowd ────────────────────────────────
 *
 * `crowd_static_01.glb` is ONE figure — a single `ARC_male_avg` in one casual
 * suit. Instanced 85k times it gives a stand where every single person is the
 * same man in the same clothes, and no amount of runtime tinting fixes that: the
 * silhouette, the pose and the outfit are baked into the mesh. It also costs
 * 700 triangles each — 59.6M triangles of crowd, for figures that are a dozen
 * pixels tall.
 *
 * `crowd_atlas.png` is 32 DISTINCT baked figures (8×4 tiles, spanning body
 * types, ages, skin tones and poses — arms crossed, arm raised, clapping, phone,
 * leaning, talking), and `crowd_billboard_placements.bin` carries a poseIndex
 * per person selecting one. Two triangles each: the same 31,635-person crowd is
 * ~63k triangles instead of 59.6M, with real variety instead of none.
 *
 * ── The framing number that must not change ─────────────────────────────────
 *
 * Tiles are baked at a FIXED WORLD SCALE: every tile frames 1.04 × 1.67 m with
 * the figure's feet on the bottom edge, so the quad must be exactly that and
 * pivot at the feet. A seated spectator only fills the lower part of their tile
 * — that is correct, and is what makes them come out at true height. Change
 * SPRITE_W/SPRITE_H and the whole crowd is the wrong size.
 *
 * All motion lives in the vertex shader (one draw call, zero CPU per frame),
 * ported from the export's `web/crowd-system.js`, which was calibrated against
 * this atlas.
 */
import {
  Color, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, NormalBlending, Object3D,
  PlaneGeometry, ShaderMaterial, Sphere, Vector2, Vector3, type Texture,
} from 'three';

/** float32 ×6 per person: posX, posY, posZ, rotZ, scale, poseIndex (Blender Z-up). */
const FLOATS_PER_INSTANCE = 6;

/** Crowd sectors used for localised cheering around the bowl. */
export const STAND_COUNT = 8;

/** The bake frame — see the header. Metres. */
export const SPRITE_W = 1.04;
export const SPRITE_H = 1.67;

/** Atlas layout (crowd_atlas.png, 2560×2048). */
const ATLAS_COLS = 8;
const ATLAS_ROWS = 4;

export interface CrowdBillboardPlacements {
  count: number;
  /** World-space (three.js Y-up) position per person. */
  offsets: Float32Array;
  /** scale, poseIndex per person. */
  misc: Float32Array;
  stands: Float32Array;
  seeds: Float32Array;
  poseCount: number;
}

/** Which of the 8 stands a spectator belongs to, from their position around the
 *  pitch centre — so the end nearest the action can react on its own. */
export function standIndexFor(x: number, z: number): number {
  const a = Math.atan2(z, x); // −π..π
  const t = (a + Math.PI) / (2 * Math.PI); // 0..1
  return Math.min(STAND_COUNT - 1, Math.floor(t * STAND_COUNT));
}

/** Deterministic 0..1 hash so a person's phase is stable across reloads (§24). */
function hash01(i: number): number {
  const s = Math.sin(i * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Parse the metadata JSON + instance binary. Throws (fail loud) on a contract
 * this parser has not been proven against — silently mis-placing 31k people, or
 * silently converting an axis twice, is far worse than refusing to load.
 */
export function parseCrowdBillboards(meta: unknown, bin: ArrayBuffer): CrowdBillboardPlacements {
  const root = meta as { format?: string; count?: number; upAxis?: string; poseCount?: number };
  if (!root.format?.startsWith('float32 x6')) {
    throw new Error(`crowdBillboards: unexpected format "${root.format ?? ''}" — proven only for "float32 x6: posX,posY,posZ,rotZ,scale,poseIndex".`);
  }
  if (!root.upAxis?.startsWith('Z')) {
    throw new Error(`crowdBillboards: unexpected upAxis "${root.upAxis ?? ''}" — this parser converts FROM Blender Z-up; a Y-up source would be double-converted.`);
  }
  const src = new Float32Array(bin);
  const count = Math.floor(src.length / FLOATS_PER_INSTANCE);
  if (count === 0) throw new Error('crowdBillboards: placement binary is empty');
  if (root.count !== undefined && root.count !== count) {
    throw new Error(`crowdBillboards: metadata count ${root.count} does not match the binary's ${count}`);
  }

  const offsets = new Float32Array(count * 3);
  const misc = new Float32Array(count * 2);
  const stands = new Float32Array(count);
  const seeds = new Float32Array(count);

  // ── Why the order is shuffled ─────────────────────────────────────────────
  // Density thinning lowers `instanceCount`, and WebGL draws instances 0..N — so
  // it keeps a PREFIX of this buffer. The export writes people grouped by
  // section, so an unshuffled prefix empties whole stands: at LOW quality two
  // thirds of the bowl simply vanishes, in one contiguous block, rather than
  // thinning out evenly. Interleaving the order first makes any prefix a uniform
  // spatial sample of the whole crowd.
  //
  // A fixed-seed LCG, never Math.random, so a reload places everyone identically
  // (§24) and the seeded per-person phases stay stable with them.
  const order = new Uint32Array(count);
  for (let i = 0; i < count; i++) order[i] = i;
  let rng = 0x9e3779b9;
  for (let i = count - 1; i > 0; i--) {
    rng = (Math.imul(rng, 1664525) + 1013904223) >>> 0;
    const j = rng % (i + 1);
    const t = order[i]!;
    order[i] = order[j]!;
    order[j] = t;
  }

  for (let i = 0; i < count; i++) {
    const o = order[i]! * FLOATS_PER_INSTANCE;
    // Blender Z-up → three.js Y-up: (x, z, −y). Same convention as the flags.
    const x = src[o]!;
    const y = src[o + 2]!;
    const z = -src[o + 1]!;
    offsets[i * 3] = x;
    offsets[i * 3 + 1] = y;
    offsets[i * 3 + 2] = z;
    misc[i * 2] = src[o + 4]!; // scale
    misc[i * 2 + 1] = src[o + 5]!; // poseIndex
    // Stand sectors are keyed in WORLD space, because `excite(x, z)` is called
    // with sim/world coordinates (the ball). The bowl mounts under V5_BOWL_YAW
    // (+90° about Y, asserted by test), which maps local (x, z) → world (z, −x);
    // sectoring on local coords would shift every reaction a quarter-bowl round.
    stands[i] = standIndexFor(z, -x);
    seeds[i] = hash01(i + 1);
  }
  return { count, offsets, misc, stands, seeds, poseCount: root.poseCount ?? ATLAS_COLS * ATLAS_ROWS };
}

const vertexShader = /* glsl */ `
  precision highp float;

  attribute vec3  iOffset;
  attribute vec2  iMisc;        // x = scale, y = poseIndex
  attribute float iStand;
  attribute float iSeed;

  uniform float uTime;
  uniform float uCheer[${STAND_COUNT}];
  uniform vec2  uAtlas;
  uniform float uAtlasCount;
  uniform float uBobAmp;
  uniform float uCheerLift;
  uniform float uDanceAmount;
  uniform float uDanceBpm;

  varying vec2  vUv;
  varying float vDepthTint;

  void main() {
    float cheer = uCheer[int(iStand)];

    // Idle breathing — everyone on their own phase and frequency, so a stand
    // never pulses in lockstep.
    float phase = iSeed * 6.2831853;
    float freq  = 0.55 + iSeed * 0.65;
    float bob   = sin(uTime * freq * 6.2831853 + phase);

    float amp  = uBobAmp * (1.0 + cheer * 6.0);
    float lift = cheer * uCheerLift * (0.6 + 0.4 * bob);

    // Dancing: a SHARED beat moves a block together, the per-person phase
    // offset stops it reading as a chorus line.
    float beat  = uTime * uDanceBpm / 60.0 * 6.2831853;
    float sway  = sin(beat * 0.5 + phase * 0.35);
    float dance = cheer * uDanceAmount;
    float lean  = sway * dance * 0.30;

    // Billboard about Y toward the camera.
    //
    // iOffset is in the BOWL's local space and MUST go through modelMatrix: the
    // crowd hangs under StadiumV5Root, which carries the +90 degree bowl yaw
    // (V5_BOWL_YAW). Skipping it leaves the crowd ring 90 degrees out from the
    // stands it belongs to — which lands it on the pitch, and empties the seats.
    // cameraPosition is world-space, so the billboard basis is built after this.
    vec3 centre = (modelMatrix * vec4(iOffset, 1.0)).xyz + vec3(0.0, bob * amp + lift, 0.0);
    vec3 toCam  = normalize(cameraPosition - centre);
    vec3 right  = normalize(cross(vec3(0.0, 1.0, 0.0), toCam));

    float s = iMisc.x;
    float cs = cos(lean), sn = sin(lean);
    vec2  q  = vec2(position.x * cs - position.y * sn,
                    position.x * sn + position.y * cs);
    vec3 world = centre
               + right * (q.x + sway * dance * 0.18) * s
               + vec3(0.0, 1.0, 0.0) * q.y * s;

    float tile = mod(iMisc.y, uAtlasCount);
    float col  = mod(tile, uAtlas.x);
    float row  = floor(tile / uAtlas.x);
    // Atlas rows are laid out top-down; flip so tile 0 is the TOP-left one.
    vUv = (uv + vec2(col, uAtlas.y - 1.0 - row)) / uAtlas;

    // Aerial haze ramp — the export's own FA_TierHaze profile: zero below
    // y=11 (tier 1 stays clean), full by y=32 (top of tier 3). Starting lower
    // washed all of tier 2 toward pale blue and it read as a faded stand.
    vDepthTint = clamp((centre.y - 11.0) / 21.0, 0.0, 1.0);

    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  uniform sampler2D uAtlasMap;
  uniform vec3  uHazeColor;
  uniform float uHazeStrength;
  uniform float uExposure;

  varying vec2  vUv;
  varying float vDepthTint;

  void main() {
    vec4 tex = texture2D(uAtlasMap, vUv);
    if (tex.a < 0.14) discard;              // alpha CUTOUT — keeps edges crisp
                                            // and needs no depth sorting
    // Un-dilute the mip chain: the atlas's transparent texels are rgba(0,0,0,0),
    // so distance mips average every figure toward BLACK in proportion to lost
    // coverage — the far tiers read as pale desaturated mush. Dividing by alpha
    // recovers the figure's own colour (straight-alpha un-premultiply).
    vec3 col = min(tex.rgb / max(tex.a, 0.001), vec3(1.0)) * uExposure;
    col = mix(col, uHazeColor, vDepthTint * uHazeStrength);
    gl_FragColor = vec4(col, 1.0);
  }
`;

/**
 * One InstancedMesh, one draw call, two triangles per spectator. Everything that
 * makes the crowd feel alive happens in the vertex shader.
 */
export class CrowdBillboardSystem {
  readonly root = new Object3D();
  private mesh: Mesh | null = null;
  private material: ShaderMaterial | null = null;
  private count = 0;
  private readonly cheer = new Float32Array(STAND_COUNT);
  private readonly target = new Float32Array(STAND_COUNT);
  private density = 1;
  private fullCount = 0;

  constructor() { this.root.name = 'CrowdBillboardSystem'; }

  build(placements: CrowdBillboardPlacements, atlas: Texture): void {
    this.dispose();
    const { count, offsets, misc, stands, seeds } = placements;

    // A single quad, pivot at the FEET — see the header's framing note.
    const quad = new PlaneGeometry(SPRITE_W, SPRITE_H);
    quad.translate(0, SPRITE_H * 0.5, 0);

    const geo = new InstancedBufferGeometry();
    geo.index = quad.index;
    geo.attributes.position = quad.attributes.position!;
    geo.attributes.uv = quad.attributes.uv!;
    geo.setAttribute('iOffset', new InstancedBufferAttribute(offsets, 3));
    geo.setAttribute('iMisc', new InstancedBufferAttribute(misc, 2));
    geo.setAttribute('iStand', new InstancedBufferAttribute(stands, 1));
    geo.setAttribute('iSeed', new InstancedBufferAttribute(seeds, 1));
    geo.instanceCount = count;
    // The bowl is static, so a generous fixed sphere beats recomputing bounds.
    geo.boundingSphere = new Sphere(new Vector3(0, 20, 0), 260);
    quad.dispose();

    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uCheer: { value: new Array<number>(STAND_COUNT).fill(0) },
        uAtlas: { value: new Vector2(ATLAS_COLS, ATLAS_ROWS) },
        uAtlasCount: { value: ATLAS_COLS * ATLAS_ROWS },
        uBobAmp: { value: 0.025 },
        uCheerLift: { value: 0.22 },
        uDanceAmount: { value: 1.0 },
        uDanceBpm: { value: 124 },
        uAtlasMap: { value: atlas },
        uHazeColor: { value: new Color(0xa8c2eb) },
        // Export README's own per-tier tint spec tops out at ×0.42 (tier 3);
        // 0.45 over a ramp that peaks mid-tier-2 was washing the stand pale.
        uHazeStrength: { value: 0.3 },
        uExposure: { value: 1.0 },
      },
      // Alpha CUTOUT via `discard`, never blending: 31k transparent quads would
      // need back-to-front sorting every frame and would still self-overlap
      // wrongly inside a stand. Opaque + discard is order-independent and free.
      transparent: false,
      blending: NormalBlending,
    });

    const mesh = new Mesh(geo, material);
    mesh.frustumCulled = false; // one draw call, and the bowl surrounds the camera
    mesh.name = 'CrowdBillboards';
    mesh.renderOrder = 0;

    this.mesh = mesh;
    this.material = material;
    this.count = count;
    this.fullCount = count;
    this.root.add(mesh);
    this.setDensity(this.density);
  }

  /** Whole ground erupts — goals. */
  roar(strength = 1): void {
    for (let i = 0; i < STAND_COUNT; i++) this.target[i] = strength;
  }

  /** Localised reaction — the stands nearest a world point react most. */
  excite(x: number, z: number, strength = 0.7, spread = 1.5): void {
    const near = standIndexFor(x, z);
    for (let i = 0; i < STAND_COUNT; i++) {
      let d = Math.abs(i - near);
      d = Math.min(d, STAND_COUNT - d); // wrap around the bowl
      const f = Math.max(0, 1 - d / spread);
      if (f > 0) this.target[i] = Math.max(this.target[i]!, strength * f);
    }
  }

  /** 0 ambient, 1 a goal-roar. Mirrors the CrowdControl contract. */
  setExcitement(v: number): void {
    this.roar(v < 0 ? 0 : v > 1 ? 1 : v);
  }

  /**
   * Per frame. Attack is fast and decay slow — that asymmetry is what reads as a
   * roar rather than a blink.
   */
  update(elapsedS: number, dtS: number): void {
    const u = this.material?.uniforms;
    if (!u) return;
    u.uTime!.value = elapsedS;
    const cheerArr = u.uCheer!.value as number[];
    for (let i = 0; i < STAND_COUNT; i++) {
      const target = this.target[i]!;
      const cheer = this.cheer[i]!;
      const rate = target > cheer ? 8.0 : 0.45;
      const next = cheer + (target - cheer) * Math.min(1, dtS * rate);
      this.cheer[i] = next;
      cheerArr[i] = next;
      this.target[i] = target * Math.max(0, 1 - dtS * 0.7);
    }
  }

  /** Quality thinning — just lowers the instance count, no rebuild. */
  setDensity(f: number): void {
    this.density = f < 0 ? 0 : f > 1 ? 1 : f;
    const geo = this.mesh?.geometry as InstancedBufferGeometry | undefined;
    if (geo) geo.instanceCount = Math.max(0, Math.floor(this.fullCount * this.density));
  }

  setEnabled(on: boolean): void { this.root.visible = on; }

  /** Match the crowd's brightness to the lighting preset. */
  setExposure(v: number): void {
    const u = this.material?.uniforms;
    if (u) u.uExposure!.value = v;
  }

  getCount(): number { return this.count; }

  dispose(): void {
    if (this.mesh) {
      this.root.remove(this.mesh);
      this.mesh.geometry.dispose();
    }
    this.material?.dispose();
    this.mesh = null;
    this.material = null;
    this.count = 0;
    this.fullCount = 0;
    this.cheer.fill(0);
    this.target.fill(0);
  }
}
